// ======================================
// Admin Projects Page Module
// ======================================

import { icons } from '../../components.js';
import { requireAuth, getUser, logout } from '../../lib/auth.js';
import { projectsAPI, usersAPI } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

export function renderAdminProjects() {
  if (!requireAuth(['admin', 'manager', 'designer'])) {
    return '<div class="loading">Redirecting...</div>';
  }

  const user = getUser();

  const content = `
          <!-- Filters -->
          <div class="admin-filters">
            <div class="filter-group">
              <select id="status-filter" class="form-select form-select-sm">
                <option value="">All Status</option>
                <option value="quote">Quote</option>
                <option value="confirmed">Confirmed</option>
                <option value="designing">Designing</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="installing">Installing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <button class="btn btn-primary btn-sm" id="add-project-btn">
              ${icons.plus || '+'} New Project
            </button>
          </div>
          
          <!-- Projects Table -->
          <div class="admin-card">
            <div class="admin-card-body" id="projects-container">
              <div class="loading-placeholder">Loading projects...</div>
            </div>
          </div>
    
    <!-- Project Detail Modal -->
    <div class="modal-overlay" id="project-modal" style="display:none;">
      <div class="modal-dialog" style="max-width:800px;">
        <div class="modal-header">
          <h2 class="modal-title" id="modal-title">Project Details</h2>
          <button class="modal-close" id="modal-close">&times;</button>
        </div>
        <div class="modal-body" id="modal-body">
          <div class="loading-placeholder">Loading...</div>
        </div>
      </div>
    </div>
  `;

  return renderAdminLayout('projects', 'Projects', content);
}

export async function initAdminProjects() {
  initAdminLayout();

  // Load projects
  await loadProjects();

  // Status filter
  const statusFilter = document.getElementById('status-filter');
  if (statusFilter) {
    statusFilter.addEventListener('change', () => loadProjects());
  }

  // Modal handlers
  const modal = document.getElementById('project-modal');
  const modalClose = document.getElementById('modal-close');

  if (modalClose) modalClose.addEventListener('click', () => closeModal());
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });
  }

  // Add project button
  const addBtn = document.getElementById('add-project-btn');
  if (addBtn) {
    addBtn.addEventListener('click', () => showNewProjectForm());
  }
}

async function loadProjects() {
  const container = document.getElementById('projects-container');
  if (!container) return;

  try {
    const statusFilter = document.getElementById('status-filter')?.value || '';
    const params = statusFilter ? { status: statusFilter } : {};
    const response = await projectsAPI.list(params);

    if (response.success && response.data?.length > 0) {
      container.innerHTML = `
        <table class="admin-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Customer</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            ${response.data.map(project => `
              <tr>
                <td>
                  <div class="lead-name">${project.title}</div>
                  <div class="lead-meta">${project.city || 'N/A'}</div>
                </td>
                <td>${project.customer_name || 'N/A'}</td>
                <td><span class="badge badge-outline">${project.property_type || 'N/A'}</span></td>
                <td class="text-nowrap">₹${formatAmount(project.total_amount)}</td>
                <td><span class="status-badge status-${project.status}">${project.status}</span></td>
                <td>
                  <div class="action-buttons">
                    <button class="btn-icon" onclick="viewProject(${project.id})" title="View">👁️</button>
                    <button class="btn-icon" onclick="editProject(${project.id})" title="Edit">✏️</button>
                  </div>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    } else {
      container.innerHTML = '<p class="empty-state">No projects found</p>';
    }
  } catch (error) {
    console.error('Failed to load projects:', error);
    container.innerHTML = '<p class="error-state">Failed to load projects</p>';
  }
}

function formatAmount(amount) {
  if (!amount) return '0';
  if (amount >= 100000) return (amount / 100000).toFixed(2) + 'L';
  if (amount >= 1000) return (amount / 1000).toFixed(0) + 'K';
  return amount.toString();
}

// Global functions for onclick handlers
window.viewProject = async function (id) {
  const modal = document.getElementById('project-modal');
  const modalBody = document.getElementById('modal-body');
  const modalTitle = document.getElementById('modal-title');

  if (!modal || !modalBody) return;

  modal.style.display = 'flex';
  modalBody.innerHTML = '<div class="loading-placeholder">Loading project details...</div>';

  try {
    const response = await projectsAPI.get(id);
    if (response.success && response.data) {
      const project = response.data;
      modalTitle.textContent = project.title;

      modalBody.innerHTML = `
        <div class="project-detail">
          <div class="detail-grid">
            <div class="detail-item">
              <label>Customer</label>
              <p>${project.customer_name || 'N/A'}</p>
            </div>
            <div class="detail-item">
              <label>Email</label>
              <p>${project.customer_email || 'N/A'}</p>
            </div>
            <div class="detail-item">
              <label>Phone</label>
              <p>${project.customer_phone || 'N/A'}</p>
            </div>
            <div class="detail-item">
              <label>Property Type</label>
              <p>${project.property_type || 'N/A'}</p>
            </div>
            <div class="detail-item">
              <label>Address</label>
              <p>${project.address}, ${project.city} ${project.pincode || ''}</p>
            </div>
            <div class="detail-item">
              <label>Total Amount</label>
              <p class="text-lg font-bold">₹${formatAmount(project.total_amount)}</p>
            </div>
            <div class="detail-item">
              <label>Paid Amount</label>
              <p>₹${formatAmount(project.paid_amount)}</p>
            </div>
            <div class="detail-item">
              <label>Designer</label>
              <p>${project.designer_name || 'Not Assigned'}</p>
            </div>
          </div>
          
          <div class="milestones-section">
            <h3>Milestones</h3>
            <div class="milestones-list">
              ${project.milestones && project.milestones.length > 0 ?
          project.milestones.map(m => `
                  <div class="milestone-item ${m.completed_at ? 'completed' : ''}">
                    <span class="milestone-icon">${m.completed_at ? '✓' : '○'}</span>
                    <span class="milestone-title">${m.title}</span>
                    ${m.completed_at ? `<span class="milestone-date">${formatDate(m.completed_at)}</span>` : ''}
                  </div>
                `).join('')
          : '<p class="empty-state">No milestones</p>'
        }
            </div>
          </div>
          
          <div class="modal-actions">
            <button class="btn btn-outline" onclick="closeModal()">Close</button>
            <button class="btn btn-primary" onclick="editProject(${project.id})">Edit Project</button>
          </div>
        </div>
      `;
    }
  } catch (error) {
    modalBody.innerHTML = '<p class="error-state">Failed to load project details</p>';
  }
};

window.editProject = async function (id) {
  const modal = document.getElementById('project-modal');
  const modalBody = document.getElementById('modal-body');
  const modalTitle = document.getElementById('modal-title');

  if (!modal || !modalBody) return;

  modal.style.display = 'flex';
  modalTitle.textContent = 'Edit Project';

  try {
    const response = await projectsAPI.get(id);
    if (response.success && response.data) {
      const project = response.data;

      modalBody.innerHTML = `
        <form id="edit-project-form">
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">Status</label>
              <select name="status" class="form-select" required>
                <option value="quote" ${project.status === 'quote' ? 'selected' : ''}>Quote</option>
                <option value="confirmed" ${project.status === 'confirmed' ? 'selected' : ''}>Confirmed</option>
                <option value="designing" ${project.status === 'designing' ? 'selected' : ''}>Designing</option>
                <option value="manufacturing" ${project.status === 'manufacturing' ? 'selected' : ''}>Manufacturing</option>
                <option value="installing" ${project.status === 'installing' ? 'selected' : ''}>Installing</option>
                <option value="completed" ${project.status === 'completed' ? 'selected' : ''}>Completed</option>
                <option value="cancelled" ${project.status === 'cancelled' ? 'selected' : ''}>Cancelled</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Paid Amount (₹)</label>
              <input type="number" name="paid_amount" class="form-input" value="${project.paid_amount || 0}">
            </div>
            <div class="form-group">
              <label class="form-label">Notes</label>
              <textarea name="notes" class="form-textarea" rows="3">${project.notes || ''}</textarea>
            </div>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-outline" onclick="closeModal()">Cancel</button>
            <button type="submit" class="btn btn-primary">Save Changes</button>
          </div>
        </form>
      `;

      document.getElementById('edit-project-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const form = e.target;
        const data = {
          status: form.status.value,
          paid_amount: parseInt(form.paid_amount.value) || 0,
          notes: form.notes.value
        };

        try {
          await projectsAPI.update(id, data);
          closeModal();
          loadProjects();
        } catch (error) {
          alert('Failed to update project');
        }
      });
    }
  } catch (error) {
    modalBody.innerHTML = '<p class="error-state">Failed to load project</p>';
  }
};

async function showNewProjectForm() {
  const modal = document.getElementById('project-modal');
  const modalBody = document.getElementById('modal-body');
  const modalTitle = document.getElementById('modal-title');

  if (!modal || !modalBody) return;

  modal.style.display = 'flex';
  modalTitle.textContent = 'Create New Project';
  modalBody.innerHTML = '<div class="loading-placeholder">Loading customers...</div>';

  try {
    const response = await usersAPI.list();
    const customers = (response.data || []).filter(u => u.role === 'customer' || u.role === 'client');

    modalBody.innerHTML = `
      <form id="create-project-form">
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Project Title *</label>
            <input type="text" name="title" class="form-input" required placeholder="e.g. Modern Villa Interior">
          </div>
          <div class="form-group">
            <label class="form-label">Customer *</label>
            <select name="customer_id" class="form-select" required>
              <option value="">Select Customer</option>
              ${customers.map(c => `<option value="${c.id}">${c.name} (${c.email})</option>`).join('')}
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Property Type</label>
            <select name="property_type" class="form-select">
              <option value="apartment">Apartment</option>
              <option value="villa">Villa</option>
              <option value="individual_house">Individual House</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Total Estimate (₹)</label>
            <input type="number" name="total_amount" class="form-input" value="0">
          </div>
          <div class="form-group">
             <label class="form-label">Status</label>
             <select name="status" class="form-select">
               <option value="quote">Quote</option>
               <option value="confirmed">Confirmed</option>
               <option value="designing">Designing</option>
             </select>
          </div>
        </div>
        <div class="form-group">
            <label class="form-label">Site Address</label>
            <textarea name="address" class="form-textarea" rows="2"></textarea>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" onclick="closeModal()">Cancel</button>
          <button type="submit" class="btn btn-primary">Create Project</button>
        </div>
      </form>
    `;

    document.getElementById('create-project-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const form = e.target;
      const data = {
        title: form.title.value,
        customer_id: form.customer_id.value,
        property_type: form.property_type.value,
        total_amount: parseInt(form.total_amount.value) || 0,
        status: form.status.value,
        address: form.address.value
      };

      try {
        await projectsAPI.create(data);
        closeModal();
        loadProjects();
      } catch (error) {
        alert('Failed to create project');
      }
    });

  } catch (error) {
    modalBody.innerHTML = '<div class="error-state"><p>Failed to load customers.</p><button class="btn btn-sm btn-outline" onclick="closeModal()">Close</button></div>';
  }
}

window.closeModal = function () {
  const modal = document.getElementById('project-modal');
  if (modal) modal.style.display = 'none';
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
